// ─────────────────────────────────────────────────────────────
// Customer-facing display (CFD) sync
//
// The POS runs on one window (cashier). The rear-monitor screen runs a
// separate window pointed at /customer-display. They talk over a
// BroadcastChannel — no server round-trip, no polling, no latency.
//
// The cashier window PUBLISHES cart snapshots as it changes.
// The customer-display window SUBSCRIBES and re-renders.
//
// BroadcastChannel is same-origin, same-machine. If the browser does not
// support it (very old ones) we fall back to a `storage` event on
// localStorage so the mechanism still works.
// ─────────────────────────────────────────────────────────────
const CHANNEL_NAME = 'pos-customer-display';
const STORAGE_KEY  = 'pos-cfd-payload';

let bc = null;
try {
  if (typeof BroadcastChannel !== 'undefined') bc = new BroadcastChannel(CHANNEL_NAME);
} catch (_) {}

export const publishCart = (payload) => {
  const packet = { t: Date.now(), ...payload };
  try { bc && bc.postMessage(packet); } catch (_) {}
  // Storage-event fallback / late-open recovery: the display window can
  // read this on mount so it picks up the current state without waiting
  // for the next cart change.
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(packet)); } catch (_) {}
};

export const readLastSnapshot = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};

// Returns an unsubscribe function.
export const subscribeCart = (onMessage) => {
  const bcHandler = (ev) => { try { onMessage(ev.data); } catch (_) {} };
  const storageHandler = (ev) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try { onMessage(JSON.parse(ev.newValue)); } catch (_) {}
  };
  if (bc) bc.addEventListener('message', bcHandler);
  window.addEventListener('storage', storageHandler);
  return () => {
    if (bc) bc.removeEventListener('message', bcHandler);
    window.removeEventListener('storage', storageHandler);
  };
};

// Called when the cashier finishes a sale — tells the display to swap to
// the "Thank you" screen for a few seconds.
export const publishThankYou = ({ total, receiptNo, change }) => {
  publishCart({ kind: 'thank-you', total, receiptNo, change });
};

// Called on a fresh POS load or when the cashier clears the cart.
export const publishIdle = () => publishCart({ kind: 'idle' });
